import { Injectable, computed, signal } from '@angular/core';

import { ExchangeNcSessionResponse } from '../../../features/admin-nc/models/nc-runtime.model';

export type NcSessionRole = ExchangeNcSessionResponse['role'];

const STORAGE_KEY_PREFIX = 'synexa.nc-session.';

/** NC assessment session — in-memory with tab-scoped sessionStorage for page refresh. */
@Injectable({ providedIn: 'root' })
export class NcSessionTokenService {
  private readonly sessionState = signal<ExchangeNcSessionResponse | null>(null);

  readonly session = this.sessionState.asReadonly();
  readonly token = computed(() => this.sessionState()?.accessToken ?? null);
  readonly encounterId = computed(() => this.sessionState()?.encounterId ?? null);
  readonly episodeId = computed(() => this.sessionState()?.episodeId ?? null);
  readonly role = computed((): NcSessionRole | null => this.sessionState()?.role ?? null);
  readonly expiresAtUtc = computed(() => this.sessionState()?.expiresAtUtc ?? null);
  readonly isActive = computed(() => {
    const session = this.sessionState();
    return !!session?.accessToken && !isNcSessionExpired(session.expiresAtUtc);
  });
  readonly isPatient = computed(() => this.role() === 'Patient');
  readonly isProvider = computed(() => this.role() === 'Provider');

  setSession(session: ExchangeNcSessionResponse): void {
    this.sessionState.set(session);
    this.persistSession(session);
  }

  /** Restore a non-expired session for this episode (e.g. after page refresh). */
  restoreSession(episodeId: string): ExchangeNcSessionResponse | null {
    const cached = this.readPersistedSession(episodeId);
    if (!cached) {
      return null;
    }
    this.sessionState.set(cached);
    return cached;
  }

  clear(): void {
    const episodeId = this.sessionState()?.episodeId;
    this.sessionState.set(null);
    if (episodeId) {
      this.removePersistedSession(episodeId);
    }
  }

  clearForEpisode(episodeId: string): void {
    if (this.sessionState()?.episodeId === episodeId) {
      this.sessionState.set(null);
    }
    this.removePersistedSession(episodeId);
  }

  private persistSession(session: ExchangeNcSessionResponse): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(storageKey(session.episodeId), JSON.stringify(session));
    } catch {
      // Ignore quota / private-mode errors — in-memory session still works until refresh.
    }
  }

  private readPersistedSession(episodeId: string): ExchangeNcSessionResponse | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(storageKey(episodeId));
      if (!raw) {
        return null;
      }
      const session = JSON.parse(raw) as ExchangeNcSessionResponse;
      if (session.episodeId !== episodeId || !session.accessToken) {
        this.removePersistedSession(episodeId);
        return null;
      }
      if (isNcSessionExpired(session.expiresAtUtc)) {
        this.removePersistedSession(episodeId);
        return null;
      }
      return session;
    } catch {
      this.removePersistedSession(episodeId);
      return null;
    }
  }

  private removePersistedSession(episodeId: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.removeItem(storageKey(episodeId));
    } catch {
      // ignore
    }
  }
}

function storageKey(episodeId: string): string {
  return `${STORAGE_KEY_PREFIX}${episodeId}`;
}

export function isNcSessionExpired(expiresAtUtc: string, skewSeconds = 30): boolean {
  const expires = Date.parse(expiresAtUtc);
  if (Number.isNaN(expires)) {
    return true;
  }
  return Date.now() >= expires - skewSeconds * 1000;
}
